/**
 * Big-moment beats: which incoming plays deserve a micro-celebration on the stage.
 * Premium sports-media, not confetti: a flash in the team colour, a score pop, a short haptic.
 * Only made baskets count; the fan's team scoring, a lead change, and a three are beats.
 */
import type { GameEvent, Side } from '@clutch/core';

export type BeatKind = 'lead_change' | 'team_three' | 'team_score' | 'tie';
export type Beat = { kind: BeatKind; side: Side; mine: boolean; strength: 1 | 2 };

const leader = (h: number, a: number): Side | 'tie' => (h === a ? 'tie' : h > a ? 'home' : 'away');

export function beatFor(
  e: Pick<GameEvent, 'side' | 'points' | 'made' | 'homeScore' | 'awayScore'>,
  prev: { homeScore: number; awayScore: number } | undefined,
  mySide?: Side,
): Beat | undefined {
  if (!e.side || !e.points || e.made === false || !prev) return undefined;
  if (e.homeScore === prev.homeScore && e.awayScore === prev.awayScore) return undefined;
  const before = leader(prev.homeScore, prev.awayScore);
  const after = leader(e.homeScore, e.awayScore);
  const mine = !!mySide && e.side === mySide;
  if (after !== 'tie' && before !== after) return { kind: 'lead_change', side: e.side, mine, strength: 2 };
  if (after === 'tie' && before !== 'tie') return { kind: 'tie', side: e.side, mine, strength: mine ? 2 : 1 };
  if (mine) return { kind: e.points === 3 ? 'team_three' : 'team_score', side: e.side, mine, strength: e.points === 3 ? 2 : 1 };
  return undefined;
}

/** Short beat label shown on the stage. */
export function beatLabel(b: Beat, teamName: string): string {
  if (b.kind === 'lead_change') return `${teamName} take the lead`;
  if (b.kind === 'tie') return 'All square';
  if (b.kind === 'team_three') return `${teamName} from deep`;
  return `${teamName} score`;
}

/** Haptic for a beat; silent where unsupported or when the fan prefers reduced motion. */
export function celebrate(strength: 'small' | 'big') {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  // Browsers block vibration before the first tap; don't spam the console on a cold open.
  const ua = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
  if (ua && !ua.hasBeenActive) return;
  try {
    navigator.vibrate(strength === 'big' ? [18, 50, 30] : 12);
  } catch {
    /* ignore */
  }
}
